import { Alert, FormControl, TextField, Typography } from '@mui/material';
import { IAOIInfo } from '../../core/interfaces/aoi-info.interface';

export interface ProjectDialogFormProps {
  AOIInfo: IAOIInfo;
  showError?: boolean;
  isInstantHabMap?: boolean;
  isReseller?: boolean;
  projectPrice?: number;
  projectName: string;
  projectDescription: string;
  resellerCustomerName?: string;
  resellerExternalProjectId?: string;
  resellerNotes?: string;
  setProjectName: (projectName: string) => void;
  setProjectDescription: (projectDescription: string) => void;
  setResellerCustomerName?: (value: string) => void;
  setResellerExternalProjectId?: (value: string) => void;
  setResellerNotes?: (value: string) => void;
  creditBlocked?: boolean;
  creditWarningMessage?: string;
}

export const ProjectDialogForm: React.FC<ProjectDialogFormProps> = ({
  AOIInfo,
  showError,
  isInstantHabMap,
  isReseller,
  projectPrice,
  projectName,
  projectDescription,
  resellerCustomerName,
  resellerExternalProjectId,
  resellerNotes,
  setProjectName,
  setProjectDescription,
  setResellerCustomerName,
  setResellerExternalProjectId,
  setResellerNotes,
  creditBlocked,
  creditWarningMessage,
}) => {
  return (
    <FormControl
      id="project-form"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: 'var(--content-spacing-narrow)',
        justifyContent: 'space-evenly',
        alignContent: 'space-evenly',
        alignItems: 'center',
        textAlign: 'center',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <img
        alt="AOI Thumbnail"
        src={AOIInfo?.thumbnail}
        style={{
          maxHeight: '128px',
          maxWidth: '256px',
          border: '1px solid var(--gentian-light-gray)',
          boxShadow: '0px 0px 4px 0px rgba(0,0,0,0.75)',
        }}
      />
      <Typography marginTop="4px" style={{ width: '100%', color: 'var(--gentian-dark-gray)' }}>
        Total Area: {AOIInfo.TotalArea} {AOIInfo.units}
      </Typography>
      {isReseller && typeof projectPrice === 'number' && (
        <Typography marginTop="4px" style={{ width: '100%', color: 'var(--gentian-dark-gray)' }}>
          Project Price: £{projectPrice.toFixed(2)}
        </Typography>
      )}
      <TextField
        id="outlined-basic"
        label="Project Name"
        variant="standard"
        fullWidth
        required
        value={projectName}
        error={
          !projectName || /[^a-zA-Z0-9 ,._-]/.test(projectName) // invalid if empty or has disallowed characters
        }
        helperText={
          /[^a-zA-Z0-9 ,._-]/.test(projectName)
            ? 'Only letters, numbers, spaces, commas, hyphens, underscores, and periods are allowed.'
            : !projectName
              ? 'Project name is required.'
              : ''
        }
        onChange={(e) => {
          setProjectName(e.target.value);
        }}
        sx={{
          margin: 'var(--content-spacing-narrow)',
          '& .MuiInput-input': {
            lineHeight: '1.75em',
            height: '1.75em',
          },
        }}
      />

      {isInstantHabMap && AOIInfo.TotalArea > 40 && AOIInfo.TotalArea <= 100 && (
        <Alert severity="info" sx={{ color: 'var(--gentian-red)' }}>
          <strong>Large Area Detected!</strong>
          <br /> Your site is over 40 hectares. <br />
          Processing may take up to one hour. <br />
          Once payment is complete, you can close the page. <br />
          Processing will continue in the background. <br />
          We’ll email you when your results are ready to view.
        </Alert>
      )}
      {isInstantHabMap && AOIInfo.TotalArea > 100 && (
        <Alert severity="info" sx={{ color: 'var(--gentian-red)' }}>
          <strong>Large Area Detected!</strong> <br />
          Your site is over 100 hectares. <br />
          Processing may take up to 24 hours. <br />
          Once payment is complete, you can close the page. <br />
          Processing will continue in the background. <br />
          We’ll email you when your results are ready to view.
        </Alert>
      )}
      {creditBlocked && (
        <Alert severity="error" sx={{ width: '100%', mt: 1 }}>
          {creditWarningMessage ||
            'You have exceeded your credit limit. Please pay your outstanding balance before creating a new project.'}
        </Alert>
      )}

      {!isInstantHabMap && !isReseller && (
        <TextField
          id="outlined-basic"
          label="Tell Us More"
          variant="standard"
          multiline
          rows={4}
          helperText="Explain your project in more detail."
          fullWidth
          onChange={(e) => {
            setProjectDescription(e.target.value);
          }}
          value={projectDescription}
          sx={{
            margin: 'var(--content-spacing-narrow)',
          }}
        />
      )}

      {isReseller && (
        <>
          <TextField
            id="reseller-customer-name"
            label="Reseller Customer Name"
            variant="standard"
            fullWidth
            value={resellerCustomerName ?? ''}
            onChange={(e) => setResellerCustomerName?.(e.target.value)}
            sx={{
              margin: 'var(--content-spacing-narrow)',
            }}
          />
          <TextField
            id="reseller-external-project-id"
            label="Reseller External Project ID"
            variant="standard"
            fullWidth
            value={resellerExternalProjectId ?? ''}
            onChange={(e) => setResellerExternalProjectId?.(e.target.value)}
            sx={{
              margin: 'var(--content-spacing-narrow)',
            }}
          />
          <TextField
            id="reseller-notes"
            label="Reseller Notes/PO Number"
            variant="standard"
            fullWidth
            multiline
            rows={3}
            value={resellerNotes ?? ''}
            onChange={(e) => setResellerNotes?.(e.target.value)}
            sx={{
              margin: 'var(--content-spacing-narrow)',
            }}
          />
        </>
      )}
    </FormControl>
  );
};
